import { LoginBodyDto } from "@/api/types/main/schemas";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useUserStore } from "@/store/user";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";

export const LoginPage = () => {
  const { login, isAuth } = useUserStore((state) => ({
    login: state.login,
    isAuth: state.isAuth,
  }));
  const location = useLocation();
  const navigate = useNavigate();
  const fromPage = location.state?.from?.pathname || "/";
  const [formError, setFormError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (isAuth) {
      navigate(fromPage);
    }
  }, [isAuth, fromPage, navigate]);

  const { mutate, isPending } = useMutation({
    mutationFn: async (variables: LoginBodyDto) => await login(variables.email, variables.password),
    onError: (error: AxiosError<{ type: string; message?: string }>) => {
      if (error.response?.data?.type === "invalid-credentials") {
        form.setError("email", {
          type: "manual",
          message: "Неверный логин или пароль.",
        });
      } else if (error.response?.data?.message === "User not found") {
        form.setError("email", {
          type: "manual",
          message: "Такой пользователь не зарегистрирован",
        });
      } else {
        setFormError("Произошла ошибка при входа. Попробуйте снова.");
      }
    },
  });

  const formSchema = z.object({
    email: z.string().min(20, {
      message: "Email не должен быть менее 20 символов.",
    }),
    // .refine((email) => email.endsWith("@champ-commerce.ru"), {
    //   message: "Email должен заканчиваться на @champ-commerce.ru",
    // }),
    password: z.string().min(6, {
      message: "Пароль должен быть не менее 6 символов.",
    }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setFormError(null);
    form.clearErrors();
    mutate(values);
  }

  return (
    <div className="flex h-screen justify-center items-center">
      <Card className="w-full max-w-sm px-2">
        <CardHeader>
          <CardTitle className="text-2xl">Вход</CardTitle>
          <CardDescription>
            Добро пожаловать в рабочее пространство Felfri! Введите корпоративную почту и пароль.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-2">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {formError && <div className="text-red-500 text-center mb-4">{formError}</div>}
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="email@champ-commerce.ru" type="email" {...field} />
                    </FormControl>
                    <FormDescription>Введите вашу корпоративную почту</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Пароль</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((prev) => !prev)}
                          className="absolute inset-y-0 right-2 flex items-center"
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4 text-gray-600" />
                          ) : (
                            <Eye className="h-4 w-4 text-gray-600" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormDescription className="flex justify-between">
                      <span>Введите пароль</span>
                      <span>
                        <span
                          className="cursor-pointer hover:text-primary transition-colors"
                          onClick={() => navigate("/reset-password")}
                        >
                          Забыли пароль?
                        </span>
                      </span>
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button className="w-full" type="submit" disabled={isPending}>
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Загрузка...
                  </>
                ) : (
                  "Войти"
                )}
              </Button>
            </form>
          </Form>
        </CardContent>
        <CardFooter className="flex-col gap-2">
          <Button className="w-full" variant={"outline"} onClick={() => navigate("/registration")}>
            Зарегистрироваться
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};
